import { FilterType } from 'src/app/components/lib/wizard/filters';

import {
  CalculationMethods,
  DiscountAppliesTo,
  ItemGroupType,
  RewardApplyTo,
  ThresholdType,
  TotalItemConditionType,
} from '../models';

import {
  formatPercentValue,
  getRequirementsFromRequirementStepState,
  percentCalculationMethods,
} from './shared/helpers';
import { type Constraint, type Restrictions, type Discount } from './types/index';
import { ApplicationMethodTypeId } from './types/index';

import type { FilterResourceType } from './shared/filter';
import type { useDiscountWizardState } from './state';
import type { Requirement } from './state/requirements';
import type { RewardsStepState } from './state/rewards';

const restrictionsObjectKeyByFilterName: Record<FilterResourceType, keyof Restrictions> = {
  brand: 'Brand',
  category: 'Category',
  'inventory tag': 'InventoryTag',
  product: 'Product',
  strain: 'Strain',
  'product tag': 'Tag',
  'pricing tier': 'Tier',
  vendor: 'Vendor',
  weight: 'Weight',
};

type WizardState = Pick<
  ReturnType<typeof useDiscountWizardState>,
  'configureStepState' | 'detailsStepState' | 'onlineDetailsStepState' | 'requirementsStepState' | 'rewardsStepState'
> & { discountId: number };

type GetRequestBodyParams = WizardState & {
  useDiscountsLoyaltyMultiplierFix?: boolean;
};

export function getRequestBody({
  configureStepState,
  detailsStepState,
  onlineDetailsStepState,
  requirementsStepState,
  rewardsStepState,
  discountId,
  useDiscountsLoyaltyMultiplierFix = false,
}: GetRequestBodyParams): Discount {
  const requirements = getRequirementsFromRequirementStepState(requirementsStepState);
  const constraints = getConstraintsFromRequirements(requirements, discountId);
  const isPercentOff = percentCalculationMethods.includes(rewardsStepState.discountMethod);
  const rewardDetailsFromConstraint = getRewardDetailsFromConstraint(rewardsStepState);

  // required fields
  const discount: Discount = {
    ApplicationMethodId: getApplicationMethodId(configureStepState.applicationMethod),
    CanStackAutomatically: rewardsStepState.allowStacking,
    Constraints: constraints,
    DiscountDescription: configureStepState.discountName,
    ExternalId: configureStepState.externalId,
    FirstTimeCustomerOnly: detailsStepState.firstTimeCustomerOption,
    Id: discountId ?? 0, // 0 for new discounts
    IgnoreNetTax: rewardsStepState.ignoreNetTaxes,
    IsAvailableOnline: detailsStepState.availableOnline,
    IsBundledDiscount: constraints.length > 1 && rewardsStepState.discountAppliesTo === DiscountAppliesTo.SameProducts,
    LocationRestrictions: detailsStepState.availableLocIds,
    LoyaltyPointValue:
      configureStepState.loyaltyPointsToRedeem === '' ? undefined : configureStepState.loyaltyPointsToRedeem,
    OnlineName: onlineDetailsStepState.onlineName || configureStepState.discountName,
    PaymentRestrictions: detailsStepState.paymentRestrictions,
    RedemptionLimit: detailsStepState.redemptionLimit,
    RequireManagerApproval: detailsStepState.requireManagerApproval,
    RestrictToGroupIds: detailsStepState.discountGroupIds,
    RestrictToSegmentIds: detailsStepState.segmentIds,
    PlatformTypeRestrictions:
      detailsStepState.platformTypeRestrictions.length > 0 ? detailsStepState.platformTypeRestrictions : [],
    Reward: {
      DiscountRewardId: rewardsStepState.condition.id,
      HasThreshold: rewardsStepState.condition.condition !== ThresholdType.NoThreshold,
      ApplyToOnlyOneItem: getApplyToOnlyOneItem(rewardsStepState),
      CalculationMethodId:
        rewardsStepState.discountMethod !== 0 ? rewardsStepState.discountMethod : CalculationMethods.AmountOff,
      DiscountValue:
        (() => {
          if (isPercentOff) {
            return formatPercentValue(rewardsStepState.percentOff);
          }
          if (
            useDiscountsLoyaltyMultiplierFix &&
            rewardsStepState.discountMethod === CalculationMethods.LoyaltyMultiplier
          ) {
            return rewardsStepState.loyaltyMultiplier;
          }
          return rewardsStepState.amountOff;
        })() || 0,
      HighestOrLowest: rewardsStepState.discountAppliesTo === DiscountAppliesTo.HighestPricedItem ? 'high' : 'low',
      IncludeNonCannabis: rewardsStepState.condition.allowNonCannabisItems,
      ItemGroupTypeId: rewardsStepState.condition.requireSameProduct ? ItemGroupType.BySKU : ItemGroupType.All,
      ManualDefaultApplyTo: rewardsStepState.manualApplyToMethod !== 0 ? rewardsStepState.manualApplyToMethod : 1,
      Restrictions: rewardDetailsFromConstraint.restrictions,
      ThresholdMax: rewardDetailsFromConstraint.thresholdMax,
      ThresholdMin: rewardDetailsFromConstraint.thresholdMin,
    },

    // if there are multiple constraints, then true
    // or if there is one constraint and rewards  "different set of products" selected, then true
    SavedWithAdvancedOptions:
      constraints.length > 1 ||
      (constraints.length === 1 && rewardsStepState.discountAppliesTo === DiscountAppliesTo.DifferentProducts),

    // if there is no end date set, use the maximum valid end date to represent "does not end"
    // LeafLogix.Web/Dutchie.Common.Domain/Models/Register/Discount/Bundle Discounts/DiscountModel.cs, :17
    ValidDateTo: detailsStepState.endDate?.toLocaleString() || new Date('4200-04-19T12:00:00').toLocaleDateString(),
    ValidDateFrom: detailsStepState.startDate?.toLocaleString() || new Date().toLocaleDateString(),
  };

  // optional / conditional fields
  if (rewardDetailsFromConstraint.thresholdTypeId) {
    discount.Reward.ThresholdTypeId = rewardDetailsFromConstraint.thresholdTypeId;
  }

  if (configureStepState.discountCode !== '') {
    discount.DiscountCode = configureStepState.discountCode;
  }
  if (detailsStepState.limitPerCustomer) {
    discount.MaxRedemptions = detailsStepState.limitPerCustomerQuantity;
  }
  if (detailsStepState.restrictByDayOfWeek) {
    discount.Sunday = detailsStepState.activeDaysOfTheWeek.Sun;
    discount.Monday = detailsStepState.activeDaysOfTheWeek.Mon;
    discount.Tuesday = detailsStepState.activeDaysOfTheWeek.Tue;
    discount.Wednesday = detailsStepState.activeDaysOfTheWeek.Wed;
    discount.Thursday = detailsStepState.activeDaysOfTheWeek.Thu;
    discount.Friday = detailsStepState.activeDaysOfTheWeek.Fri;
    discount.Saturday = detailsStepState.activeDaysOfTheWeek.Sat;
  }
  if (detailsStepState.startTime !== '') {
    discount.StartTime = detailsStepState.startTime;
  }
  if (detailsStepState.endTime !== '') {
    discount.EndTime = detailsStepState.endTime;
  }

  if (onlineDetailsStepState.rank !== '') {
    discount.MenuDisplayRank = onlineDetailsStepState.rank;
  }

  // If there's exactly one requirement and a valid apply method was selected,
  // move the restrictions object to the Rewards' Restrictions object and delete the constraint
  const validApplyToMethod =
    rewardsStepState.discountAppliesTo === DiscountAppliesTo.HighestPricedItem ||
    rewardsStepState.discountAppliesTo === DiscountAppliesTo.LowestPricedItem ||
    rewardsStepState.discountAppliesTo === DiscountAppliesTo.SameProducts;

  if (discount.Constraints.length === 1 && validApplyToMethod) {
    const { Restrictions: applicableRestrictions } = discount.Constraints[0];
    const { ...constraintDetails } = constraints[0];
    discount.Reward.Restrictions = { ...applicableRestrictions };
    discount.Reward.ThresholdMax = constraintDetails.ThresholdMax;
    discount.Reward.ThresholdMin = constraintDetails.ThresholdMin;
    discount.Reward.IncludeNonCannabis = constraintDetails.IncludeNonCannabis;
    discount.Reward.HasThreshold = constraintDetails.HasThreshold;
    discount.Reward.ThresholdTypeId = constraintDetails.ThresholdTypeId;
    discount.Reward.ItemGroupTypeId = constraintDetails.DiscountItemGroupTypeId ?? ItemGroupType.All;

    discount.Constraints = [];
  }

  const { imageUrl, onlineName, description } = onlineDetailsStepState;
  if (imageUrl !== '' || onlineName !== '' || description !== '') {
    discount.DiscountMenuDisplayDetails = {
      DiscountId: discountId,
      MenuDisplayImageUrl: onlineDetailsStepState.imageUrl ?? '',
      MenuDisplayName: onlineDetailsStepState.onlineName || configureStepState.discountName,
      MenuDisplayDescription: onlineDetailsStepState.description,
      DiscountMenuDisplayId: onlineDetailsStepState.displayId,
    };
  }

  // we must set the CustomerType restriction when there are any customer types selected after we set
  // the other restrictions that come from the requirements/rewards step to avoid it being overridden
  if (detailsStepState.customerTypeIds.length > 0) {
    discount.Reward.Restrictions.CustomerType = {
      IsExclusion: false,
      RestrictionIds: detailsStepState.customerTypeIds,
    };
  }

  return discount;
}

function getApplicationMethodId(applicationMethod: number) {
  if (applicationMethod === ApplicationMethodTypeId.ExternalLoyalty) {
    return ApplicationMethodTypeId.Manually;
  }

  if (applicationMethod === ApplicationMethodTypeId.DutchieLoyalty) {
    return ApplicationMethodTypeId.ByCode;
  }

  return applicationMethod;
}

function getApplyToOnlyOneItem({ discountAppliesTo, condition }: RewardsStepState) {
  const applyDiscountToIsHighestPricedItem = discountAppliesTo === DiscountAppliesTo.HighestPricedItem;
  const applyDiscountToIsLowestPricedItem = discountAppliesTo === DiscountAppliesTo.LowestPricedItem;

  if (applyDiscountToIsHighestPricedItem || applyDiscountToIsLowestPricedItem) {
    return true;
  }

  const applyDiscountToIsDifferentProducts = discountAppliesTo === DiscountAppliesTo.DifferentProducts;
  const cartContainsIsExactly = condition.totalItemCondition === TotalItemConditionType.Exactly;
  const appliesToIsHighOrLow = condition.applyTo === RewardApplyTo.high || condition.applyTo === RewardApplyTo.low;

  if (applyDiscountToIsDifferentProducts && cartContainsIsExactly && appliesToIsHighOrLow) {
    return true;
  }

  return false;
}

function getRewardDetailsFromConstraint({ condition }: RewardsStepState) {
  const conditionFilters = Object.entries(condition.filters);
  const restrictions: Restrictions = {};

  conditionFilters.forEach(([filterName, filterValue]) => {
    if (filterValue.selectionType !== FilterType.NONE) {
      const key = restrictionsObjectKeyByFilterName[filterName as FilterResourceType];
      restrictions[key] = {
        IsExclusion: filterValue.selectionType === FilterType.EXCLUSIONARY,
        RestrictionIds: filterValue.resources.map((resource) => resource.resourceId),
      };
    }
  });

  if (condition.condition !== ThresholdType.NoThreshold) {
    let minimum: number | undefined;
    let maximum: number | undefined;

    switch (condition.condition) {
      case ThresholdType.SpendingAmount:
        minimum = Number(condition.minimumSpend);
        break;
      case ThresholdType.ProductWeight:
        minimum = Number(condition.minimumGrams);
        break;
      case ThresholdType.NumberOfItems:
        minimum = Number(condition.minimumItems);
        switch (condition.totalItemCondition) {
          case TotalItemConditionType.AtLeast:
            maximum = 10000;
            break;
          case TotalItemConditionType.Between:
            maximum = Number(condition.maximumItems);
            break;
          case TotalItemConditionType.Every:
            break;
          case TotalItemConditionType.Exactly:
            maximum = Number(condition.minimumItems);
        }
    }
    return {
      restrictions,
      thresholdMin: minimum,
      thresholdMax: maximum,
      thresholdTypeId: condition.condition,
    };
  }

  return {
    restrictions,
    thresholdMin: undefined,
    thresholdMax: undefined,
    thresholdTypeId: condition.condition,
  };
}

function getConstraintsFromRequirements(requirements: Record<string, Requirement>, discountId: number) {
  return (
    Object.values(requirements)
      // If a requirement has no threshold and no filters,
      // it's not a "constraint" according to the back end
      // and should not be in the Constraints request payload array
      .filter(
        (requirement) =>
          requirement.condition !== ThresholdType.NoThreshold ||
          Object.values(requirement.filters).some((filter) => filter.selectionType !== FilterType.NONE)
      )
      .map((requirement) => {
        const constraint: Constraint & { HasThreshold: boolean } = {
          DiscountId: discountId,
          DiscountItemGroupTypeId: requirement.requireSameProduct ? ItemGroupType.BySKU : ItemGroupType.All,
          IncludeNonCannabis: requirement.allowNonCannabisItems,
          Restrictions: {},
          HasThreshold: true,
          ThresholdMax: null,
          DiscountConstraintId: requirement.id,
        };

        if (requirement.condition !== ThresholdType.NoThreshold) {
          constraint.ThresholdTypeId = requirement.condition;
        }

        switch (requirement.condition) {
          case ThresholdType.NoThreshold:
            break;
          case ThresholdType.SpendingAmount:
            constraint.ThresholdMin = Number(requirement.minimumSpend);
            break;
          case ThresholdType.ProductWeight:
            constraint.ThresholdMin = Number(requirement.minimumGrams);
            break;
          case ThresholdType.NumberOfItems:
            constraint.ThresholdMin = Number(requirement.minimumItems);
            switch (requirement.totalItemCondition) {
              case TotalItemConditionType.AtLeast:
                constraint.ThresholdMax = 10000;
                break;
              case TotalItemConditionType.Between:
                constraint.ThresholdMax = Number(requirement.maximumItems);
                break;
              case TotalItemConditionType.Every:
                constraint.ThresholdMax = null;
                break;
              case TotalItemConditionType.Exactly:
                constraint.ThresholdMax = Number(requirement.minimumItems);
            }
        }

        Object.entries(requirement.filters).forEach(([filterName, filterValue]) => {
          if (filterValue.selectionType !== FilterType.NONE) {
            const key = restrictionsObjectKeyByFilterName[filterName as FilterResourceType];
            constraint.Restrictions[key] = {
              IsExclusion: filterValue.selectionType === FilterType.EXCLUSIONARY,
              RestrictionIds: filterValue.resources.map((resource) => resource.resourceId),
            };
          }
        });

        return constraint;
      })
  );
}
